import React, { useMemo, useState } from 'react';
import { Button, Dropdown, InputNumber, Modal, Space, Tooltip, Typography, message } from 'antd';
import {
  DownloadOutlined,
  FileProtectOutlined,
  FileTextOutlined,
  FileZipOutlined,
} from '@ant-design/icons';
import { usePDF } from '@react-pdf/renderer';
import { TemplateRenderer, useTemplateOrFallback } from 'PDF/TemplateEngine';
import dayjs from 'dayjs';
import { useInformeAnualEdificio, usePaqueteDOMEdificio } from 'providers/Edificios';

const { Text } = Typography;

// Formatea fechas YYYY-MM-DD para columnas de tabla y footer, evitando que
// @react-pdf/renderer reinterprete Date() con timezone local.
const fmtDate = (d) => (d ? dayjs(d).format('DD/MM/YYYY') : '—');

const enrichInformePayload = (raw) => {
  if (!raw) return null;
  return {
    cliente_nombre: raw.cliente?.name ?? '—',
    cliente_rut: '—',
    periodo_desde: raw.periodo?.desde ?? '',
    periodo_hasta: raw.periodo?.hasta ?? '',
    total_mantenciones: raw.mantenciones?.length ?? 0,
    mantenciones: (raw.mantenciones ?? []).map((m) => ({
      id: m.id,
      closed_at_fmt: fmtDate(m.closed_at),
      tecnico_nombre: m.tecnico_nombre ?? '—',
      tecnico_rut: m.tecnico_rut ?? '—',
      observaciones: m.observaciones ?? '',
    })),
  };
};

const enrichPaquetePayload = (raw) => {
  if (!raw) return null;
  return {
    cliente_nombre: raw.cliente?.name ?? '—',
    edificio_nombre: raw.edificio?.nombre ?? '—',
    edificio_direccion: raw.edificio?.direccion ?? '—',
    edificio_comuna: raw.edificio?.comuna ?? '—',
    edificio_ciudad: raw.edificio?.ciudad ?? '—',
    edificio_rol_predio: raw.edificio?.rol_predio ?? '—',
    edificio_tipo_uso: raw.edificio?.tipo_uso ?? '—',
    edificio_num_unidades: raw.edificio?.num_unidades ?? '—',
    edificio_fecha_recepcion_dom: fmtDate(raw.edificio?.fecha_recepcion_dom),
    edificio_ano_construccion: raw.edificio?.ano_construccion ?? '—',
    total_equipos: raw.total_equipos ?? 0,
    fecha_emision_paquete: fmtDate(raw.fecha_emision_paquete),
    equipos: (raw.equipos ?? []).map((e) => ({
      tipo_label: e.tipo_equipo_categoria ?? '—',
      nombre: e.nombre ?? '—',
      num_registro_minvu: e.num_registro_minvu ?? '—',
      fabricante: e.fabricante ?? '—',
      marca: `${e.marca ?? '—'} / ${e.modelo ?? '—'}`,
      ano_instalacion: e.ano_instalacion ?? '—',
      estado: e.estado ?? '—',
    })),
    certificaciones: (raw.certificaciones ?? []).map((c) => ({
      equipo_nombre: c.equipo_nombre ?? '—',
      tipo: c.tipo,
      fecha_emision_fmt: fmtDate(c.fecha_emision),
      fecha_vencimiento_fmt: fmtDate(c.fecha_vencimiento),
      entidad_certificadora: c.entidad_certificadora,
      num_certificado: c.num_certificado ?? '—',
      estado: c.estado,
    })),
    mantenciones: (raw.mantenciones ?? []).map((m) => ({
      closed_at_fmt: fmtDate(m.closed_at),
      equipo_nombre: m.equipo_nombre ?? '—',
      tecnico_nombre: m.tecnico_nombre ?? '—',
      tecnico_rut: m.tecnico_rut ?? '—',
      observaciones: m.observaciones ?? '',
    })),
  };
};

// Genera el PDF en background y expone solo los controles de descarga /
// abrir en nueva pestaña. Se evita el preview con <iframe> porque un blob
// URL cargado en iframe sobre HTTP dispara un warning de "insecure connection"
// en Chrome — para el dev server local no vale la pena.
function PdfDownloader({ template, data, fileName, loading }) {
  const document = useMemo(() => {
    // El template puede venir sin `id` cuando es el default (generado por el
    // backend pero no persistido). Lo aceptamos igual — basta con tener
    // `sections` para renderizar.
    if (!template || !Array.isArray(template.sections) || !data) return null;
    return <TemplateRenderer template={template} data={data} config={{}} />;
  }, [template, data]);

  const [instance, updateInstance] = usePDF({ document });
  React.useEffect(() => {
    if (document) updateInstance(document);
  }, [document, updateInstance]);

  if (loading || instance.loading) {
    return (
      <Space direction="vertical" style={{ width: '100%', padding: '16px 0' }}>
        <Text type="secondary">Generando PDF…</Text>
      </Space>
    );
  }
  if (instance.error) {
    return <Text type="danger">Error al generar PDF: {String(instance.error)}</Text>;
  }
  if (!instance.url) {
    return <Text type="secondary">Preparando PDF…</Text>;
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Text type="secondary">
        PDF generado. Descargalo o abrilo en una pestaña nueva para previsualizar.
      </Text>
      <Space wrap>
        <a href={instance.url} download={fileName}>
          <Button type="primary" icon={<DownloadOutlined />}>
            Descargar {fileName}
          </Button>
        </a>
        <Button
          icon={<FileTextOutlined />}
          onClick={() => window.open(instance.url, '_blank', 'noopener,noreferrer')}
        >
          Abrir en nueva pestaña
        </Button>
      </Space>
    </Space>
  );
}

// Botón-doble para generar el Informe Anual o el Paquete Carpeta Cero DOM
// desde una fila de edificio. Ambos docs consumen un payload del backend y
// lo renderizan client-side con el template low-code correspondiente.
function EdificioDocumentActions({ edificio }) {
  const [mode, setMode] = useState(null); // 'informe' | 'paquete'
  const [ano, setAno] = useState(new Date().getFullYear());
  const [rawPayload, setRawPayload] = useState(null);

  const { fetchInforme, loading: loadingInforme } = useInformeAnualEdificio();
  const { fetchPaquete, loading: loadingPaquete } = usePaqueteDOMEdificio();

  const { template: informeTemplate, loading: loadingTplInforme } = useTemplateOrFallback(
    'informe_anual_mantenciones'
  );
  const { template: paqueteTemplate, loading: loadingTplPaquete } = useTemplateOrFallback(
    'paquete_carpeta_cero_dom'
  );

  const openInforme = async () => {
    setMode('informe');
    setRawPayload(null);
    try {
      const payload = await fetchInforme(edificio.id, ano);
      setRawPayload(payload);
    } catch (err) {
      message.error('No se pudo cargar el informe anual');
    }
  };
  const openPaquete = async () => {
    setMode('paquete');
    setRawPayload(null);
    try {
      const payload = await fetchPaquete(edificio.id);
      setRawPayload(payload);
    } catch (err) {
      message.error('No se pudo cargar el Paquete DOM');
    }
  };

  const close = () => {
    setMode(null);
    setRawPayload(null);
  };

  const enriched = useMemo(() => {
    if (!rawPayload) return null;
    return mode === 'informe' ? enrichInformePayload(rawPayload) : enrichPaquetePayload(rawPayload);
  }, [rawPayload, mode]);

  const items = [
    {
      key: 'informe',
      icon: <FileTextOutlined />,
      label: 'Informe Anual de Mantenciones',
      onClick: openInforme,
    },
    {
      key: 'paquete',
      icon: <FileProtectOutlined />,
      label: 'Paquete Carpeta Cero DOM',
      onClick: openPaquete,
    },
  ];

  const activeLoading =
    (mode === 'informe' && (loadingInforme || loadingTplInforme)) ||
    (mode === 'paquete' && (loadingPaquete || loadingTplPaquete));

  const activeTemplate = mode === 'informe' ? informeTemplate : paqueteTemplate;
  const fileName =
    mode === 'informe'
      ? `informe-anual-${edificio.nombre?.replace(/\s+/g, '-') ?? edificio.id}-${ano}.pdf`
      : `paquete-dom-${edificio.nombre?.replace(/\s+/g, '-') ?? edificio.id}.pdf`;

  return (
    <>
      <Dropdown menu={{ items }} trigger={['click']}>
        <Tooltip title="Generar informe o paquete DOM">
          <Button icon={<FileZipOutlined />} size="small" />
        </Tooltip>
      </Dropdown>

      <Modal
        title={
          mode === 'informe'
            ? `Informe Anual — ${edificio.nombre ?? edificio.id}`
            : `Paquete Carpeta Cero DOM — ${edificio.nombre ?? edificio.id}`
        }
        open={!!mode}
        onCancel={close}
        footer={null}
        width={840}
        destroyOnHidden
      >
        {mode === 'informe' && (
          <Space style={{ marginBottom: 12 }}>
            <Text>Año:</Text>
            <InputNumber
              min={2000}
              max={new Date().getFullYear() + 1}
              value={ano}
              onChange={setAno}
            />
            <Button onClick={openInforme} loading={loadingInforme}>
              Recargar datos
            </Button>
          </Space>
        )}
        <PdfDownloader
          template={activeTemplate}
          data={enriched}
          fileName={fileName}
          loading={activeLoading}
        />
      </Modal>
    </>
  );
}

export default EdificioDocumentActions;
